import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { Logo } from "@/components/Logo";

const LAST_UPDATED = "February 17, 2026";

export default function Terms() {
  return (
    <div className="min-h-screen bg-[#FBFAF6]">
      <nav className="px-5 sm:px-8 lg:px-12 py-4 flex items-center justify-between border-b border-slate-200/60 bg-white/80 backdrop-blur sticky top-0 z-40">
        <Link to="/" className="flex items-center gap-2.5">
          <Logo size={36} withWordmark />
        </Link>
        <Link to="/" className="text-sm font-semibold text-slate-600 hover:text-slate-900">Back to home</Link>
      </nav>

      <main className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 py-14 lg:py-20" data-testid="terms-page">
        <div className="text-xs font-bold uppercase tracking-widest text-[#C75D33] mb-3">Legal</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight leading-[1.05]">Terms of Service</h1>
        <p className="text-slate-500 mt-3 text-sm">Last updated: {LAST_UPDATED}</p>

        <div className="prose prose-slate mt-10 max-w-none">
          <Section title="1. Welcome">
            <p>
              WindowScreenQuote.com (&ldquo;<strong>WindowScreenQuote</strong>&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;) operates a website and software platform that (a) lets homeowners request instant pricing for window screen jobs and connects them with a local screen company, and (b) provides screen companies with a white-label instant-quote funnel and lead management tools (the &ldquo;<strong>Service</strong>&rdquo;).
            </p>
            <p>
              By using the Service, you agree to these Terms of Service (&ldquo;<strong>Terms</strong>&rdquo;). If you do not agree, do not use the Service.
            </p>
          </Section>

          <Section title="2. Who we are and what we are not">
            <p>
              <strong>WindowScreenQuote is not a screen company.</strong> We do not manufacture, sell, install, or service window screens. We provide an online tool that estimates pricing based on information you provide and on rates set by independent local screen companies (each, a &ldquo;<strong>Screen Company</strong>&rdquo;).
            </p>
            <p>
              Screen Companies that use the Service are independent businesses. They are responsible for their own pricing, scheduling, workmanship, warranties, licensing, insurance, and any contract they sign with you.
            </p>
          </Section>

          <Section title="3. Quotes are estimates, not contracts">
            <p>
              Any price you see on the Service is an <strong>estimate</strong> based on the information you submit and the rates a Screen Company has configured in our platform. Final pricing may change after an in-person measurement, depending on actual job conditions (frame size, mesh type, accessibility, location, taxes, fees, etc.).
            </p>
            <p>
              A quote is not a binding offer or a contract. A binding agreement is formed only when you and a Screen Company sign or otherwise agree to a separate work agreement.
            </p>
          </Section>

          <Section title="4. Use of the Service by homeowners">
            <p>By submitting a quote request you confirm that:</p>
            <ul>
              <li>The information you provide is accurate and is your own.</li>
              <li>You are at least 18 years old and have authority to request work at the property.</li>
              <li>You agree that the Screen Company that receives your quote may contact you by phone, text, or email to confirm details, schedule a measurement, and complete the job.</li>
              <li>You will use the Service only for personal, non-commercial purposes related to your own property or one you are authorized to manage.</li>
            </ul>
          </Section>

          <Section title="5. Use of the Service by screen companies">
            <p>
              If you sign up for a Screen Company account, you additionally agree that:
            </p>
            <ul>
              <li>You operate a legitimate window screen business, licensed and insured as required by applicable law.</li>
              <li>You will keep your pricing, services, and business information accurate and up to date.</li>
              <li>You will only contact homeowner leads about the screen job they requested and will comply with all applicable consumer-protection, anti-spam, telemarketing, and Do-Not-Call laws.</li>
              <li>You will honor the quote you give a homeowner in good faith, or clearly explain any deviation before charging them.</li>
              <li>You are solely responsible for any contract, payment, refund, dispute, workmanship issue, or warranty between you and the homeowner.</li>
              <li>You will pay the subscription fee for your selected plan on the recurring schedule shown at checkout until you cancel.</li>
            </ul>
          </Section>

          <Section title="6. Accounts and security">
            <p>
              You are responsible for keeping your login credentials confidential and for any activity under your account. Notify us immediately at the contact address below if you suspect unauthorized access.
            </p>
          </Section>

          <Section title="6.1 SMS / text messaging communications">
            <p>
              By submitting a form or creating an account, and providing a mobile number, you consent to receive transactional SMS communications from WindowScreenQuote.com related to:
            </p>
            <ul>
              <li>Quote requests you submit through the Service;</li>
              <li>Appointment coordination and scheduling related to those quotes;</li>
              <li>Account notifications (for Screen Companies: new lead alerts and operational alerts);</li>
              <li>Customer service responses to inquiries you send us.</li>
            </ul>
            <p>
              These messages are <strong>transactional</strong>, not promotional. <strong>Message frequency varies.</strong> Message and data rates may apply.
            </p>
            <p>
              <strong>To opt out:</strong> reply <strong>STOP</strong>, <strong>STOPALL</strong>, <strong>UNSUBSCRIBE</strong>, <strong>CANCEL</strong>, <strong>END</strong>, or <strong>QUIT</strong> to any text message we send you. You will receive one final confirmation that you have been opted out and we will not send further texts to that number unless you opt back in.
            </p>
            <p>
              <strong>For help:</strong> reply <strong>HELP</strong> or contact <a href="mailto:support@windowscreenquote.com">support@windowscreenquote.com</a>.
            </p>
            <p>
              <strong>To opt back in:</strong> reply <strong>START</strong>, <strong>YES</strong>, <strong>SUBSCRIBE</strong>, or <strong>UNSTOP</strong>.
            </p>
            <p>
              SMS consent is not a condition of any purchase. We do not share mobile information you provided to enroll in WindowScreenQuote.com SMS with third parties or affiliates for marketing or promotional purposes. See our <Link to="/privacy" className="underline">Privacy Policy</Link> for full details.
            </p>
          </Section>

          <Section title="7. Subscription billing (Screen Companies)">
            <p>
              Paid plans for Screen Companies are billed in advance on a recurring basis (currently monthly) through our payments processor. Promotional pricing — such as &ldquo;Early Adopter&rdquo; pricing — applies only as described at the time of signup and may include a trial period at a reduced rate. After the trial period the standard rate for your plan begins.
            </p>
            <p>
              You can cancel your subscription at any time from your billing settings. Cancellation takes effect at the end of the current billing period. Except where required by law, fees already paid are non-refundable.
            </p>
            <p>
              We may change pricing for new sign-ups at any time. Where we change the price of an existing subscription, we will give you reasonable advance notice.
            </p>
          </Section>

          <Section title="8. Acceptable use">
            <p>You agree not to:</p>
            <ul>
              <li>Submit false, fraudulent, or misleading information through the Service.</li>
              <li>Attempt to access another company&rsquo;s account, leads, or data.</li>
              <li>Use the Service to harass, spam, or unlawfully market to homeowners.</li>
              <li>Reverse engineer, scrape, or interfere with the Service except as allowed by law.</li>
              <li>Use the Service in a way that violates any law or third-party right.</li>
            </ul>
            <p>We may suspend or terminate access for any violation of these Terms.</p>
          </Section>

          <Section title="9. Intellectual property">
            <p>
              The Service, including its software, design, content, and branding, is owned by WindowScreenQuote and its licensors and is protected by intellectual-property laws. We grant you a limited, non-exclusive, non-transferable license to use the Service for its intended purpose. Content a Screen Company uploads (logo, pricing, mesh images, etc.) remains owned by that Screen Company; by uploading it the Screen Company grants us a license to display and process it as needed to operate the Service.
            </p>
          </Section>

          <Section title="10. Disclaimers">
            <p>
              The Service is provided <strong>&ldquo;as is&rdquo; and &ldquo;as available&rdquo;</strong>, without warranties of any kind, whether express or implied, including but not limited to merchantability, fitness for a particular purpose, accuracy, non-infringement, or quiet enjoyment. We do not warrant that quotes shown will match the final price charged by a Screen Company, or that any Screen Company will perform work to your satisfaction.
            </p>
          </Section>

          <Section title="11. Limitation of liability">
            <p>
              To the maximum extent permitted by law, WindowScreenQuote will not be liable for any indirect, incidental, special, consequential, or punitive damages, or for lost profits or revenues, arising out of or related to your use of the Service. Our total liability to you for any claim relating to the Service will not exceed the greater of (a) the amount you paid us in the twelve months before the claim arose, or (b) one hundred US dollars (US$100).
            </p>
          </Section>

          <Section title="12. Indemnification">
            <p>
              You will indemnify and hold harmless WindowScreenQuote, its officers, employees, and contractors, from any claim, loss, or expense (including reasonable attorneys&rsquo; fees) arising out of your use of the Service, your violation of these Terms, or your violation of any law or third-party right. Screen Companies further agree to indemnify us for any claim by a homeowner relating to a job they performed.
            </p>
          </Section>

          <Section title="13. Third-party services">
            <p>
              The Service uses third-party providers, including payment processing (Stripe), email delivery (Resend), and infrastructure providers. Their handling of your data is governed by their own terms and privacy policies.
            </p>
          </Section>

          <Section title="14. Termination">
            <p>
              You may stop using the Service at any time. We may suspend or terminate your access for any violation of these Terms, for non-payment, or where we reasonably believe it is necessary to protect the Service or another user. On termination, your right to use the Service ends, but sections of these Terms intended to survive termination will remain in effect.
            </p>
          </Section>

          <Section title="15. Changes to these Terms">
            <p>
              We may update these Terms from time to time. If we make a material change we will give reasonable notice (for example, by email or in-app notice). Continued use after the change means you accept the updated Terms.
            </p>
          </Section>

          <Section title="16. Governing law">
            <p>
              These Terms are governed by the laws of the State of California, USA, without regard to its conflict-of-laws rules. The exclusive venue for any dispute that is not subject to arbitration is the state or federal courts located in Riverside County, California, and you consent to personal jurisdiction there.
            </p>
          </Section>

          <Section title="17. Contact">
            <p>
              Questions about these Terms? Email us at <a href="mailto:support@windowscreenquote.com">support@windowscreenquote.com</a>.
            </p>
          </Section>
        </div>

        <div className="mt-14 pt-6 border-t border-slate-200 text-sm text-slate-500 flex flex-wrap gap-5">
          <Link to="/" className="hover:text-slate-900">Home</Link>
          <Link to="/privacy" className="hover:text-slate-900">Privacy Policy</Link>
          <Link to="/contact" className="hover:text-slate-900">Contact</Link>
          <Link to="/login" className="hover:text-slate-900">Pro login</Link>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-10">
      <h2 className="font-display text-xl sm:text-2xl font-bold text-slate-900 tracking-tight mb-3">{title}</h2>
      <div className="text-slate-700 text-base leading-relaxed space-y-3">{children}</div>
    </section>
  );
}
